import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Building2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import jsPDF from 'jspdf';
import { RUBICON_LOGO_BASE64 } from '../utils/pdfAssets';
import { getEntityDocs, PERSON_LABEL } from '../data/entityOnboardingConfig';
import { fileUrl } from '../utils/api';

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 border-b border-slate-100 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-900 text-right">{value || '—'}</span>
  </div>
);

const DocLink = ({ label, url }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 text-sm">
    <span className="text-slate-600">{label}</span>
    {url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline flex items-center gap-1 flex-shrink-0">
        View <ExternalLink size={12} />
      </a>
    ) : <span className="text-slate-400">Not provided</span>}
  </div>
);

const NonIndividualView = ({ record, publicToken = null }) => {
  const navigate = useNavigate();
  const isPublic = !!publicToken;
  const docs = getEntityDocs(record.entity_type);
  const personLabel = PERSON_LABEL[record.entity_type] || 'Authorised Person';
  const persons = record.authorised_persons || [];

  const downloadPDF = () => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = 15;
    const ensure = (h = 8) => { if (y + h > pageH - 30) { doc.addPage(); y = 15; } };

    // Centered logo (same as Individual onboarding)
    try {
      const logoWidth = 50;
      const logoHeight = 48;
      doc.addImage(RUBICON_LOGO_BASE64, 'JPEG', (pageW - logoWidth) / 2, y, logoWidth, logoHeight);
      y += 52;
    } catch (e) { y += 20; }

    // Title + subtitle
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(0, 0, 0);
    doc.text('NON-INDIVIDUAL ONBOARDING FORM', pageW / 2, y, { align: 'center' });
    y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text('KYC & Account Opening Documentation', pageW / 2, y, { align: 'center' });
    y += 12;

    const section = (title) => {
      ensure(12); doc.setFillColor(240, 244, 250); doc.rect(15, y - 4, pageW - 30, 8, 'F');
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(20, 40, 90);
      doc.text(title, 17, y + 1.5); y += 10;
    };
    const line = (label, value) => {
      ensure(6); doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(90, 90, 90);
      doc.text(String(label), 17, y);
      doc.setFont('helvetica', 'bold'); doc.setTextColor(30, 30, 30);
      doc.text(String(value || '-'), 80, y); y += 6;
    };
    const linkLine = (label, url) => {
      ensure(6); doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(90, 90, 90);
      doc.text(String(label), 17, y);
      if (url) { doc.setTextColor(30, 90, 200); doc.textWithLink('View / Download', 80, y, { url }); }
      else { doc.setTextColor(150, 150, 150); doc.text('Not provided', 80, y); }
      y += 6;
    };

    section(`Entity Details (${record.entity_type || ''})`);
    line('Entity Name', record.entity_name);
    line('Entity PAN', record.entity_pan_number);
    line('Date of Incorporation', record.date_of_incorporation);
    line('Registered Address', [record.registered_address, record.city, record.state, record.pincode].filter(Boolean).join(', '));
    line('Email', record.email); line('Phone', record.phone);

    section('Entity Bank Details');
    line('Bank Name', record.bank_name); line('Account Number', record.account_number);
    line('Account Type', record.account_type); line('IFSC', record.ifsc);
    line('MICR', record.micr); line('Branch', record.branch);

    persons.forEach((p, i) => {
      section(`${personLabel} ${i + 1}`);
      line('Name', p.name); line('Designation', p.designation);
      line('PAN', p.pan_number); line('Aadhaar', p.aadhaar_number);
      linkLine('PAN Document', fileUrl(p.pan_doc, publicToken)); linkLine('Aadhaar Document', fileUrl(p.aadhaar_doc, publicToken)); linkLine('Photograph', fileUrl(p.photo, publicToken));
    });

    section('Entity Documents');
    docs.forEach((d) => linkLine(d.label, fileUrl((record.documents || {})[d.key], publicToken)));

    // Footer with firm address & contact on every page (same as Individual)
    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      const footerY = pageH - 25;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
      doc.text('6A Dr.Rajendra Prasad Sarani, 4th Floor, R.No-20, Kolkata-700001', pageW / 2, footerY, { align: 'center' });
      doc.text('Email: communications@rubiconfinservices.com | Tel: +91-33-40442577', pageW / 2, footerY + 4, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }

    doc.save(`Onboarding_${(record.entity_name || 'entity').replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto" data-testid="non-individual-view">
        <div className="flex justify-between items-center mb-6">
          {!isPublic ? (
            <Button variant="outline" onClick={() => navigate('/admin/onboarding')}>
              <ArrowLeft size={20} className="mr-2" /> Back to Onboarding List
            </Button>
          ) : <div />}
          <Button onClick={downloadPDF} className="bg-green-600 hover:bg-green-700" data-testid="download-entity-pdf">
            <Download size={18} className="mr-2" /> Download PDF
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="text-blue-600" /> {record.entity_name || 'Entity'}
            </CardTitle>
            <p className="text-sm text-slate-500">{record.entity_type} · Non-Individual</p>
          </CardHeader>
          <CardContent>
            <Row label="Entity PAN" value={record.entity_pan_number} />
            <Row label="Date of Incorporation" value={record.date_of_incorporation} />
            <Row label="Registered Address" value={[record.registered_address, record.city, record.state, record.pincode].filter(Boolean).join(', ')} />
            <Row label="Email" value={record.email} />
            <Row label="Phone" value={record.phone} />
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader><CardTitle>Entity Bank Details</CardTitle></CardHeader>
          <CardContent>
            <Row label="Bank Name" value={record.bank_name} />
            <Row label="Account Number" value={record.account_number} />
            <Row label="Account Type" value={record.account_type} />
            <Row label="IFSC" value={record.ifsc} />
            <Row label="MICR" value={record.micr} />
            <Row label="Branch" value={record.branch} />
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader><CardTitle>{personLabel}s</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {persons.map((p, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  {p.photo && <img src={fileUrl(p.photo, publicToken)} alt={p.name} className="w-20 h-20 object-cover rounded-lg border" />}
                  <div className="flex-1">
                    <Row label="Name" value={p.name} />
                    <Row label="Designation" value={p.designation} />
                    <Row label="PAN" value={p.pan_number} />
                    <Row label="Aadhaar" value={p.aadhaar_number} />
                    <DocLink label="PAN Document" url={fileUrl(p.pan_doc, publicToken)} />
                    <DocLink label="Aadhaar Document" url={fileUrl(p.aadhaar_doc, publicToken)} />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader><CardTitle>Entity Documents</CardTitle></CardHeader>
          <CardContent>
            {docs.map((d) => <DocLink key={d.key} label={d.label} url={fileUrl((record.documents || {})[d.key], publicToken)} />)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NonIndividualView;
